// @ts-check
import { log } from '../utils/console.js';
import * as helper from './helper.js';
import { createElement } from '../helpers/dom-elements.js';
import { KEY_CODE, AUTOTESTS_ID_ATTRIBUTE } from '../constants/index.js';
import { getSiteConfiguration } from '../site-configuration.js';
import SimpleBar from '../custom-scroll.js';
import { createPesIcon } from '../../utils/utils';

const MODAL_CLASS = {
  OPEN: 'cc-modal_show',
  CLOSE: 'cc-modal_close',
  DEFAULT: 'cc-modal',
};
const BACKDROP_CLASS = {
  OPEN: 'cc-modal-backdrop_show',
  CLOSE: 'cc-modal-backdrop_close',
  DEFAULT: 'cc-modal-backdrop',
};
const SCROLL_OFFSET = {
  MOBILE: {
    TOP: '20',
    BOTTOM: '20',
  },
};

let backdropDOM;
let modalDOM;
let modalScrollBodyDOM;

const _onOpenedCallback = helper.noop;
let _onClosedCallback = helper.noop;
let _body;
let _scrollbarWidth;

/**
 *
 * @param {HTMLElement | HTMLElement[]} body
 * @param {string} modalTitle
 * @param {import('./get-settings').CommerceConnectorOptions} options
 * @returns {HTMLElement | null}
 */
function open(body, modalTitle, options) {
  if (isOpened()) {
    log.warn('Modal is already opened');
    return null;
  }

  initModal(modalTitle, options);

  _body = body;

  if (Array.isArray(body)) {
    body.forEach((b) => {
      modalScrollBodyDOM.appendChild(b);
    });
  } else if (body) {
    modalScrollBodyDOM.appendChild(body);
  }

  backdropDOM.classList.remove(BACKDROP_CLASS.CLOSE);
  if (!backdropDOM.classList.contains(BACKDROP_CLASS.OPEN)) {
    backdropDOM.classList.add(BACKDROP_CLASS.OPEN);
  }

  modalDOM.classList.remove(MODAL_CLASS.CLOSE);
  if (!modalDOM.classList.contains(MODAL_CLASS.OPEN)) {
    modalDOM.classList.add(MODAL_CLASS.OPEN);
  }

  _onOpenedCallback(); // return old body

  return modalDOM;
}

function close() {
  backdropDOM.classList.remove(BACKDROP_CLASS.OPEN);
  if (!backdropDOM.classList.contains(BACKDROP_CLASS.CLOSE)) {
    backdropDOM.classList.add(BACKDROP_CLASS.CLOSE);
  }

  modalDOM.classList.remove(MODAL_CLASS.OPEN);
  if (!modalDOM.classList.contains(MODAL_CLASS.CLOSE)) {
    modalDOM.classList.add(MODAL_CLASS.CLOSE);
  }

  if (Array.isArray(_body)) {
    _body.forEach((b) => {
      modalScrollBodyDOM.removeChild(b);
    });
  } else {
    modalScrollBodyDOM.removeChild(_body);
  }

  _onClosedCallback();
}

/**
 * If no modal on the page the function create this one.
 *
 * @param {string} modalTitle
 * @param {import('./get-settings').CommerceConnectorOptions} options
 */
function initModal(modalTitle, options) {
  if (!backdropDOM) {
    const modalContentDOM = createElement('div', { className: 'cc-modal__content' });

    const { enableRightToLeftLayout } = getSiteConfiguration();

    const simpleBar = new SimpleBar(modalContentDOM, {
      autoHide: false,
      verticalOffsetTop: SCROLL_OFFSET.MOBILE.TOP,
      verticalOffsetBottom: SCROLL_OFFSET.MOBILE.BOTTOM,
      ...(enableRightToLeftLayout && { direction: 'rtl' }),
    });

    modalScrollBodyDOM = modalContentDOM.querySelector(
      '.cc-modal__content>.simplebar-scroll-content>.simplebar-content',
    );

    const modalDialog = createElement('div', {
      className: 'cc-modal__dialog',
      contents: [renderModalHeader(modalTitle), modalContentDOM],
    });

    _scrollbarWidth = simpleBar.scrollbarWidth;

    modalDOM = createElement('div', {
      className: MODAL_CLASS.DEFAULT,
      contents: modalDialog,
    });

    backdropDOM = createElement('div', { className: BACKDROP_CLASS.DEFAULT });

    if (options && options.classList) {
      options.classList.split(' ').forEach((item) => {
        modalDOM.classList.add(item);
      });
    }

    document.body.appendChild(modalDOM);
    document.body.appendChild(backdropDOM);

    // Subscriptions
    modalDOM.addEventListener('click', close);
    backdropDOM.addEventListener('click', close);
    modalDialog.addEventListener('click', (e) => {
      e.stopPropagation();
      return true;
    });

    if (options && options.closeOnEsc) {
      document.body.addEventListener('keyup', onBodyKeyUp);
    }
  }
}

/**
 * Build modal header.
 *
 * @param {string} titleTxt
 * @returns {Element}
 */
function renderModalHeader(titleTxt) {
  const closeButton = createElement('button', {
    className: 'cc-btn-close button-less icon-button',
    type: 'button',
    attributes: {
      [AUTOTESTS_ID_ATTRIBUTE]: 'commerce-connector-modal-close-button',
    },
    contents: createElement('span', {
        className: 'vertical_middle',
        contents: createPesIcon({
            iconName: 'action_delete_cross',
            size: 'SMALL',
        })
    })
  });

  closeButton.addEventListener('click', close);

  const contents = [
    createElement('div', {
      className: 'cc-modal__header-title',
      innerHTML: titleTxt,
    }),
    closeButton,
  ];

  return createElement('div', {
    className: 'cc-modal__header',
    contents,
  });
}

/**
 * @function onBodyKeyUp
 * @param {KeyboardEvent} e
 * @description key up listener. Close window on Esc.
 */
function onBodyKeyUp(e) {
  if (e.keyCode === KEY_CODE.escape) {
    close();
  }
}

function onClosed(cb) {
  if (typeof cb !== 'function') {
    log.error('Callback should be a function');
    return;
  }

  _onClosedCallback = cb;
}

function isOpened() {
  return modalDOM && modalDOM.classList.contains(MODAL_CLASS.OPEN);
}

function maxContentHeight() {
  const paddingTop = helper.getComputedStyle(modalDOM, 'padding-top');
  const paddingBottom = helper.getComputedStyle(modalDOM, 'padding-bottom');
  const headerHeight = modalDOM.querySelector('.cc-modal__header').offsetHeight;

  return Math.floor(modalDOM.offsetHeight - paddingTop - headerHeight - paddingBottom);
}

// TODO: CartTransfer remove when Simplebar will be updated
function getScrollbarWidth() {
  return _scrollbarWidth;
}

export { open, close, isOpened, onClosed, maxContentHeight, getScrollbarWidth };
